export const blogData = {
  categories: [
    { slug: 'python', name: 'Python', count: 4 },
    { slug: 'javascript', name: 'JavaScript', count: 3 },
    { slug: 'automation', name: 'Automation', count: 1 },
    { slug: 'api-tools', name: 'API Tools', count: 3 },
    { slug: 'web-scraping', name: 'Web Scraping', count: 2 },
    { slug: 'testing', name: 'Testing', count: 1 }
  ],

  articles: [
    {
      id: 1,
      slug: 'python-requests-proxy-integration',
      title: 'How to Use IP9 Proxies with Python Requests Library',
      excerpt: 'Complete guide to integrating IP9 residential and mobile proxies with Python requests for web scraping and API calls.',
      content: `# How to Use IP9 Proxies with Python Requests Library

The Python requests library is one of the most popular tools for making HTTP requests. Integrating it with IP9 proxies enhances your web scraping capabilities and helps avoid blocks and rate limits.

## Quick Setup

First, install the required library:

\`\`\`bash
pip install requests
\`\`\`

## Basic Proxy Configuration

Here's how to configure IP9 proxies with requests:

\`\`\`python
import requests

# IP9 proxy configuration
proxies = {
    'http': 'http://username:password@endpoint:port',
    'https': 'http://username:password@endpoint:port'
}

# Make request through proxy
response = requests.get('https://httpbin.org/ip', proxies=proxies)
print(response.json())
\`\`\`

## Advanced Configuration

### Session-based Requests

For better performance and connection pooling:

\`\`\`python
import requests

session = requests.Session()
session.proxies = {
    'http': 'http://username:password@endpoint:port',
    'https': 'http://username:password@endpoint:port'
}

# All requests through this session will use the proxy
response = session.get('https://example.com')
\`\`\`

### Error Handling and Retries

\`\`\`python
import requests
from requests.adapters import HTTPAdapter
from urllib3.util.retry import Retry

def create_session_with_retries():
    session = requests.Session()
    
    retry_strategy = Retry(
        total=3,
        backoff_factor=1,
        status_forcelist=[429, 500, 502, 503, 504],
    )
    
    adapter = HTTPAdapter(max_retries=retry_strategy)
    session.mount("http://", adapter)
    session.mount("https://", adapter)
    
    session.proxies = {
        'http': 'http://username:password@endpoint:port',
        'https': 'http://username:password@endpoint:port'
    }
    
    return session
\`\`\`

## Best Practices

1. **Always use sessions** for multiple requests
2. **Implement proper error handling** for proxy failures
3. **Rotate endpoints** for better performance
4. **Set appropriate timeouts** to avoid hanging requests
5. **Use residential proxies** for better success rates

## Common Issues and Solutions

### SSL Certificate Verification
If you encounter SSL issues:

\`\`\`python
response = requests.get('https://example.com', 
                       proxies=proxies, 
                       verify=False)
\`\`\`

### Custom Headers
Add realistic headers to avoid detection:

\`\`\`python
headers = {
    'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
    'Accept-Language': 'en-US,en;q=0.5',
    'Accept-Encoding': 'gzip, deflate',
    'Connection': 'keep-alive',
}

response = requests.get('https://example.com', 
                       proxies=proxies, 
                       headers=headers)
\`\`\`

## Conclusion

Integrating IP9 proxies with Python requests is straightforward and powerful for web scraping projects. The combination provides excellent reliability and helps maintain anonymity while accessing web resources.`,
      author: 'Alex Chen',
      authorRole: 'Senior Developer',
      publishDate: '2024-12-15',
      readTime: '8 min read',
      category: 'python',
      tags: ['python', 'requests', 'web-scraping', 'tutorial'],
      featured: true,
      image: '/academy.png'
    },
    
    {
      id: 2,
      slug: 'selenium-webdriver-proxy-setup',
      title: 'Selenium WebDriver with IP9 Proxies: Complete Setup Guide',
      excerpt: 'Learn how to configure Selenium WebDriver with IP9 proxies for automated browser testing and web scraping.',
      content: `# Selenium WebDriver with IP9 Proxies: Complete Setup Guide

Selenium WebDriver is essential for browser automation and testing. Combining it with IP9 proxies adds an extra layer of anonymity and helps avoid detection in automated workflows.

## Installation

\`\`\`bash
pip install selenium
# Also install your preferred browser driver (ChromeDriver, GeckoDriver, etc.)
\`\`\`

## Chrome Configuration

\`\`\`python
from selenium import webdriver
from selenium.webdriver.chrome.options import Options

def create_chrome_driver_with_proxy():
    chrome_options = Options()
    
    # IP9 proxy configuration
    proxy_server = "http://username:password@endpoint:port"
    chrome_options.add_argument(f'--proxy-server={proxy_server}')
    
    # Additional stealth options
    chrome_options.add_argument('--no-sandbox')
    chrome_options.add_argument('--disable-dev-shm-usage')
    chrome_options.add_argument('--disable-blink-features=AutomationControlled')
    chrome_options.add_experimental_option("excludeSwitches", ["enable-automation"])
    chrome_options.add_experimental_option('useAutomationExtension', False)
    
    driver = webdriver.Chrome(options=chrome_options)
    driver.execute_script("Object.defineProperty(navigator, 'webdriver', {get: () => undefined})")
    
    return driver
\`\`\`

## Firefox Configuration

\`\`\`python
from selenium import webdriver
from selenium.webdriver.firefox.options import Options
from selenium.webdriver.common.proxy import Proxy, ProxyType

def create_firefox_driver_with_proxy():
    proxy = Proxy()
    proxy.proxy_type = ProxyType.MANUAL
    proxy.http_proxy = "endpoint:port"
    proxy.ssl_proxy = "endpoint:port"
    
    capabilities = webdriver.DesiredCapabilities.FIREFOX
    proxy.add_to_capabilities(capabilities)
    
    firefox_options = Options()
    firefox_options.add_argument('--headless')  # Optional: run in background
    
    driver = webdriver.Firefox(desired_capabilities=capabilities, options=firefox_options)
    return driver
\`\`\`

## Best Practices

1. **Always close drivers** properly to avoid resource leaks
2. **Use explicit waits** instead of time.sleep()
3. **Implement retry logic** for proxy failures
4. **Rotate user agents** for better stealth
5. **Monitor proxy health** and switch when needed

This setup provides a robust foundation for using IP9 proxies with Selenium WebDriver for various automation tasks.`,
      author: 'Sarah Rodriguez',
      authorRole: 'Automation Engineer',
      publishDate: '2024-12-12',
      readTime: '12 min read',
      category: 'automation',
      tags: ['selenium', 'webdriver', 'automation', 'testing'],
      featured: true,
      image: '/academy.png'
    },

    {
      id: 3,
      slug: 'scrapy-spider-proxy-integration',
      title: 'Building Scrapy Spiders with IP9 Proxy Rotation',
      excerpt: 'Master Scrapy spider development with IP9 proxy rotation for large-scale web scraping projects.',
      content: `# Building Scrapy Spiders with IP9 Proxy Rotation

Scrapy is a powerful web scraping framework, and when combined with IP9 proxies, it becomes an unstoppable tool for large-scale data extraction.

## Installation and Setup

pip install scrapy scrapy-rotating-proxies

## Basic Proxy Configuration

Create a settings.py file:

# settings.py

# Enable proxy rotation middleware
ROTATING_PROXY_LIST_PATH = 'proxy_list.txt'

DOWNLOADER_MIDDLEWARES = {
    'rotating_proxies.middlewares.RotatingProxyMiddleware': 610,
    'rotating_proxies.middlewares.BanDetectionMiddleware': 620,
}

# IP9 proxy settings
CONCURRENT_REQUESTS = 32
CONCURRENT_REQUESTS_PER_DOMAIN = 8
DOWNLOAD_DELAY = 1
RANDOMIZE_DOWNLOAD_DELAY = 0.5

# User agent rotation
USER_AGENT_LIST = [
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
    'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36'
]

## Proxy List Configuration

Create proxy_list.txt:

http://username:password@endpoint1:port
http://username:password@endpoint2:port
http://username:password@endpoint3:port

## Custom Spider Example

import scrapy
from scrapy import Request

class ProductSpider(scrapy.Spider):
    name = 'product_spider'
    start_urls = ['https://example-store.com/products']
    
    custom_settings = {
        'CONCURRENT_REQUESTS': 16,
        'DOWNLOAD_DELAY': 2,
    }
    
    def start_requests(self):
        for url in self.start_urls:
            yield Request(
                url=url,
                callback=self.parse,
                meta={
                    'proxy_rotation': True,
                    'max_retry_times': 3
                }
            )
    
    def parse(self, response):
        # Extract product links
        product_links = response.css('.product-item a::attr(href)').getall()
        
        for link in product_links:
            yield response.follow(
                link,
                callback=self.parse_product,
                meta={'proxy_rotation': True}
            )
        
        # Follow pagination
        next_page = response.css('.pagination .next::attr(href)').get()
        if next_page:
            yield response.follow(
                next_page,
                callback=self.parse,
                meta={'proxy_rotation': True}
            )
    
    def parse_product(self, response):
        yield {
            'name': response.css('h1.product-title::text').get(),
            'price': response.css('.price::text').get(),
            'description': response.css('.description::text').get(),
            'availability': response.css('.stock-status::text').get(),
            'url': response.url,
            'scraped_at': scrapy.utils.misc.datetime.now().isoformat()
        }

## Running Your Spider

# Run spider with custom settings
scrapy crawl product_spider -s CONCURRENT_REQUESTS=16

# Run with output to file
scrapy crawl product_spider -o products.json

# Run with specific proxy configuration
scrapy crawl product_spider -s ROTATING_PROXY_LIST_PATH=./proxy_list.txt

## Best Practices

1. Monitor proxy health and remove dead proxies
2. Implement proper retry logic for failed requests
3. Use appropriate delays to avoid being detected
4. Rotate user agents along with proxies
5. Handle pagination carefully with proxy rotation
6. Monitor success rates and adjust concurrency accordingly

This setup provides a robust framework for large-scale web scraping with IP9 proxies and Scrapy.`,
      author: 'Michael Kim',
      authorRole: 'Data Engineer',
      publishDate: '2024-12-10',
      readTime: '15 min read',
      category: 'web-scraping',
      tags: ['scrapy', 'python', 'web-scraping', 'proxy-rotation'],
      featured: false,
      image: '/academy.png'
    },

    {
      id: 4,
      slug: 'nodejs-axios-proxy-setup',
      title: 'Node.js Axios HTTP Client with IP9 Proxies',
      excerpt: 'Complete guide to using IP9 proxies with Node.js Axios for API calls and web scraping.',
      content: `# Node.js Axios HTTP Client with IP9 Proxies

Axios is the most popular HTTP client for Node.js. This guide shows you how to integrate it seamlessly with IP9 proxies for enhanced privacy and reliability.

## Installation

\`\`\`bash
npm install axios https-proxy-agent
\`\`\`

## Basic Setup

\`\`\`javascript
const axios = require('axios');
const HttpsProxyAgent = require('https-proxy-agent');

// IP9 proxy configuration
const proxyUrl = 'http://username:password@endpoint:port';
const agent = new HttpsProxyAgent(proxyUrl);

// Create axios instance with proxy
const client = axios.create({
  httpsAgent: agent,
  timeout: 30000,
  headers: {
    'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
  }
});

// Make request through proxy
client.get('https://httpbin.org/ip')
  .then(response => {
    console.log('Current IP:', response.data);
  })
  .catch(error => {
    console.error('Error:', error.message);
  });
\`\`\`

## Best Practices

1. Use environment variables for proxy configuration
2. Implement proper error handling for proxy failures
3. Monitor response times and proxy performance
4. Rotate proxies automatically to avoid rate limits
5. Log all requests for debugging and analysis

This comprehensive setup enables thorough API integration with IP9 proxies using Node.js and Axios.`,
      author: 'David Park',
      authorRole: 'Backend Developer',
      publishDate: '2024-12-08',
      readTime: '13 min read',
      category: 'javascript',
      tags: ['nodejs', 'axios', 'javascript', 'api-integration'],
      featured: false,
      image: '/academy.png'
    },

    {
      id: 5,
      slug: 'postman-proxy-configuration',
      title: 'Configuring Postman with IP9 Proxies for API Testing',
      excerpt: 'Step-by-step guide to set up IP9 proxies in Postman for secure API testing and development.',
      content: `# Configuring Postman with IP9 Proxies for API Testing

Postman is an essential tool for API development and testing. Integrating it with IP9 proxies adds security and helps test APIs from different geographical locations.

## Setting Up Proxy in Postman

### Global Proxy Configuration

1. Open Postman and go to Settings (gear icon in top right)
2. Click on Proxy tab
3. Configure the following settings:

Proxy Server: your-ip9-endpoint.com
Port: 8080
Username: your-username
Password: your-password

4. Check Use custom proxy configuration
5. Add domains to bypass if needed (e.g., localhost, 127.0.0.1)

### Collection-Level Proxy Setup

For specific collections, you can set proxy in pre-request scripts:

// Pre-request Script
pm.request.proxy = {
    host: 'your-ip9-endpoint.com',
    port: 8080,
    auth: {
        type: 'basic',
        basic: {
            username: 'your-username',
            password: 'your-password'
        }
    }
};

## Environment Variables for Proxy Management

Create environment variables for easy proxy switching:

// Environment Variables
PROXY_HOST = your-ip9-endpoint.com
PROXY_PORT = 8080
PROXY_USERNAME = your-username
PROXY_PASSWORD = your-password

Use in pre-request script:

pm.request.proxy = {
    host: pm.environment.get('PROXY_HOST'),
    port: parseInt(pm.environment.get('PROXY_PORT')),
    auth: {
        type: 'basic',
        basic: {
            username: pm.environment.get('PROXY_USERNAME'),
            password: pm.environment.get('PROXY_PASSWORD')
        }
    }
};

## Best Practices

1. Always validate proxy functionality with test endpoints
2. Use environment variables for easy proxy management
3. Implement retry logic for proxy failures
4. Monitor response times and proxy performance
5. Rotate proxies regularly to avoid detection
6. Test from multiple geographic locations when relevant
7. Keep proxy credentials secure using Postman vault

This setup allows you to effectively test APIs through IP9 proxies with full control over geographic location and request routing.`,
      author: 'Lisa Chen',
      authorRole: 'QA Engineer',
      publishDate: '2024-12-05',
      readTime: '10 min read',
      category: 'api-tools',
      tags: ['postman', 'api-testing', 'proxy-configuration'],
      featured: false,
      image: '/academy.png'
    },

    {
      id: 6,
      slug: 'curl-proxy-commands',
      title: 'Master cURL with IP9 Proxies: Essential Commands',
      excerpt: 'Essential cURL commands and techniques for using IP9 proxies in command-line HTTP operations.',
      content: `# Master cURL with IP9 Proxies: Essential Commands

cURL is a powerful command-line tool for transferring data with URLs. When combined with IP9 proxies, it becomes an essential tool for testing, debugging, and data collection.

## Basic Proxy Configuration

### Simple Proxy Usage

Basic proxy request:
\`\`\`bash
curl --proxy http://username:password@endpoint:port https://httpbin.org/ip
\`\`\`

Using HTTPS proxy:
\`\`\`bash
curl --proxy https://username:password@endpoint:port https://api.example.com
\`\`\`

Short form with -x flag:
\`\`\`bash
curl -x http://username:password@endpoint:port https://httpbin.org/ip
\`\`\`

### Authentication Methods

Basic authentication:
\`\`\`bash
curl --proxy http://endpoint:port --proxy-user username:password https://httpbin.org/ip
\`\`\`

Using environment variables:
\`\`\`bash
export https_proxy=http://username:password@endpoint:port
curl https://httpbin.org/ip
\`\`\`

## Best Practices

1. Always use timeouts to prevent hanging requests
2. Implement retry logic for critical operations
3. Rotate proxies to avoid rate limiting
4. Use realistic headers to avoid detection
5. Monitor response times and proxy health
6. Store credentials securely using environment variables
7. Log requests for debugging and monitoring

This comprehensive guide covers everything you need to effectively use cURL with IP9 proxies for various HTTP operations.`,
      author: 'Tom Wilson',
      authorRole: 'DevOps Engineer',
      publishDate: '2024-12-02',
      readTime: '11 min read',
      category: 'api-tools',
      tags: ['curl', 'command-line', 'http', 'testing'],
      featured: false,
      image: '/academy.png'
    },

    {
      id: 8,
      slug: 'beautiful-soup-web-scraping',
      title: 'Beautiful Soup Web Scraping with IP9 Proxy Integration',
      excerpt: 'Comprehensive guide to web scraping with Beautiful Soup and IP9 proxies for reliable data extraction.',
      content: `# Beautiful Soup Web Scraping with IP9 Proxy Integration

Beautiful Soup is a Python library for parsing HTML and XML documents. When combined with IP9 proxies and the requests library, it becomes a powerful tool for web scraping at scale.

## Installation and Setup

\`\`\`bash
pip install beautifulsoup4 requests lxml html5lib
\`\`\`

## Basic Setup with Proxies

\`\`\`python
import requests
from bs4 import BeautifulSoup
import time
import random
from urllib.parse import urljoin, urlparse

class ProxyScraper:
    def __init__(self, proxies):
        self.proxies = proxies
        self.current_proxy_index = 0
        self.session = requests.Session()
        self.setup_session()
    
    def setup_session(self):
        """Configure session with headers and timeouts"""
        self.session.headers.update({
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            'Accept-Language': 'en-US,en;q=0.5',
            'Accept-Encoding': 'gzip, deflate',
            'Connection': 'keep-alive',
        })
        
        # Set proxy
        self.rotate_proxy()
    
    def rotate_proxy(self):
        """Rotate to next proxy in the list"""
        proxy_config = self.proxies[self.current_proxy_index]
        self.session.proxies = {
            'http': proxy_config,
            'https': proxy_config
        }
        self.current_proxy_index = (self.current_proxy_index + 1) % len(self.proxies)
        print(f"Using proxy: {proxy_config}")
    
    def get_soup(self, url, parser='html.parser'):
        """Fetch URL and return BeautifulSoup object"""
        try:
            response = self.session.get(url, timeout=30)
            response.raise_for_status()
            
            # Create BeautifulSoup object
            soup = BeautifulSoup(response.content, parser)
            return soup
            
        except requests.exceptions.RequestException as e:
            print(f"Error fetching {url}: {e}")
            # Try with different proxy
            self.rotate_proxy()
            return None
    
    def random_delay(self, min_delay=1, max_delay=3):
        """Add random delay between requests"""
        delay = random.uniform(min_delay, max_delay)
        time.sleep(delay)

# Configure IP9 proxies
proxies = [
    'http://username:password@endpoint1:port',
    'http://username:password@endpoint2:port',
    'http://username:password@endpoint3:port'
]

scraper = ProxyScraper(proxies)
\`\`\`

## Best Practices

1. **Respect robots.txt** and website terms of service
2. **Use appropriate delays** between requests
3. **Rotate proxies regularly** to avoid IP blocks
4. **Handle errors gracefully** with retry logic
5. **Parse HTML efficiently** with appropriate parsers
6. **Store data incrementally** to avoid losing progress
7. **Monitor success rates** and proxy health
8. **Use realistic headers** to avoid detection

This comprehensive guide provides robust web scraping capabilities using Beautiful Soup with IP9 proxies for reliable data extraction.`,
      author: 'Chris Johnson',
      authorRole: 'Data Scientist',
      publishDate: '2024-11-25',
      readTime: '18 min read',
      category: 'web-scraping',
      tags: ['beautiful-soup', 'python', 'web-scraping', 'data-extraction'],
      featured: false,
      image: '/academy.png'
    },

    {
      id: 9,
      slug: 'chrome-extension-proxy-setup',
      title: 'Building Chrome Extensions with IP9 Proxy Support',
      excerpt: 'Create powerful Chrome extensions that leverage IP9 proxies for enhanced functionality and privacy.',
      content: `# Building Chrome Extensions with IP9 Proxy Support

Chrome extensions can benefit greatly from proxy integration, enabling features like geo-specific content access, enhanced privacy, and automated tasks. This guide shows how to integrate IP9 proxies into your Chrome extensions.

## Manifest Configuration

Manifest V3 Setup:

\`\`\`json
{
  "manifest_version": 3,
  "name": "IP9 Proxy Extension",
  "version": "1.0",
  "description": "Chrome extension with IP9 proxy integration",
  "permissions": [
    "proxy",
    "storage",
    "activeTab",
    "declarativeNetRequest"
  ],
  "host_permissions": [
    "<all_urls>"
  ],
  "background": {
    "service_worker": "background.js"
  },
  "action": {
    "default_popup": "popup.html",
    "default_title": "IP9 Proxy Control"
  }
}
\`\`\`

## Background Script Implementation

The background script manages proxy configuration and rotation:

\`\`\`javascript
class ProxyManager {
  constructor() {
    this.proxies = [];
    this.currentIndex = 0;
    this.isEnabled = false;
    this.init();
  }
  
  async init() {
    const result = await chrome.storage.sync.get(['proxies', 'isEnabled']);
    this.proxies = result.proxies || [];
    this.isEnabled = result.isEnabled || false;
    
    if (this.isEnabled && this.proxies.length > 0) {
      this.setProxy();
    }
  }
  
  async toggleProxy() {
    this.isEnabled = !this.isEnabled;
    await chrome.storage.sync.set({ isEnabled: this.isEnabled });
    
    if (this.isEnabled && this.proxies.length > 0) {
      this.setProxy();
    } else {
      this.clearProxy();
    }
    
    return this.isEnabled;
  }
  
  setProxy() {
    const proxy = this.proxies[this.currentIndex];
    if (!proxy) return;
    
    const config = {
      mode: "fixed_servers",
      rules: {
        singleProxy: {
          scheme: "http",
          host: proxy.host,
          port: parseInt(proxy.port)
        },
        bypassList: ["localhost", "127.0.0.1"]
      }
    };
    
    chrome.proxy.settings.set(
      { value: config, scope: 'regular' },
      () => {
        console.log('Proxy set:', proxy.host + ':' + proxy.port);
        this.updateIcon('active');
      }
    );
  }
  
  clearProxy() {
    chrome.proxy.settings.clear({ scope: 'regular' }, () => {
      console.log('Proxy cleared');
      this.updateIcon('inactive');
    });
  }
}
\`\`\`

## Best Practices

1. Store credentials securely using Chrome's storage API
2. Implement proper error handling for network failures
3. Monitor proxy health and rotate automatically
4. Respect rate limits and add appropriate delays
5. Provide clear user feedback about proxy status
6. Handle authentication properly with webRequest API
7. Test across different websites for compatibility

This extension provides a solid foundation for integrating IP9 proxies into Chrome browser functionality.`,
      author: 'Rachel Kim',
      authorRole: 'Frontend Developer',
      publishDate: '2024-11-22',
      readTime: '14 min read',
      category: 'javascript',
      tags: ['chrome-extension', 'javascript', 'browser', 'proxy-integration'],
      featured: false,
      image: '/academy.png'
    },

    {
      id: 10,
      slug: 'api-testing-with-insomnia',
      title: 'API Testing with Insomnia and IP9 Proxies',
      excerpt: 'Advanced API testing techniques using Insomnia REST client with IP9 proxy integration for comprehensive testing.',
      content: `# API Testing with Insomnia and IP9 Proxies

Insomnia is a powerful REST client that makes API testing intuitive and efficient. Integrating it with IP9 proxies enables testing from different geographical locations and enhanced privacy during development.

## Setting Up Insomnia with Proxies

### Global Proxy Configuration

1. Open Insomnia and go to Preferences (Cmd/Ctrl + ,)
2. Navigate to Proxy tab
3. Configure your IP9 proxy settings:

\`\`\`
HTTP Proxy: http://endpoint.ip9.io:8080
HTTPS Proxy: http://endpoint.ip9.io:8080
No Proxy: localhost,127.0.0.1,.local
\`\`\`

### Environment Variables for Proxy Management

Create environment variables for easy proxy switching:

\`\`\`json
{
  "base_url": "https://api.example.com",
  "proxy_host": "endpoint.ip9.io",
  "proxy_port": "8080",
  "proxy_username": "your-username",
  "proxy_password": "your-password"
}
\`\`\`

## Environment-Based Proxy Switching

Create different environments for different proxy locations:

US Environment:
\`\`\`json
{
  "name": "US Testing",
  "base_url": "https://api.example.com",
  "proxy_host": "us-endpoint.ip9.io",
  "proxy_port": "8080",
  "region": "US"
}
\`\`\`

EU Environment:
\`\`\`json
{
  "name": "EU Testing",
  "base_url": "https://api.example.com",
  "proxy_host": "eu-endpoint.ip9.io",
  "proxy_port": "8080",
  "region": "EU"
}
\`\`\`

## Testing Workflows

### Geo-location Testing Workflow

Create a folder structure for geo-testing:

1. IP Verification Request:
\`\`\`
GET https://httpbin.org/ip
\`\`\`

2. Regional API Access Test:
\`\`\`
GET {{ base_url }}/api/content
Headers:
  Authorization: Bearer {{ access_token }}
  X-Region: {{ region }}
\`\`\`

3. Localized Content Test:
\`\`\`
GET {{ base_url }}/api/products
Query Parameters:
  country: {{ region_code }}
  currency: {{ region_currency }}
\`\`\`

## Best Practices

1. Use environment variables for proxy configuration
2. Implement proper error handling for proxy failures
3. Monitor response times and proxy performance
4. Rotate proxies automatically to avoid rate limits
5. Log all requests for debugging and analysis
6. Test from multiple regions for geo-specific features
7. Validate proxy health regularly
8. Use realistic headers to avoid detection

This comprehensive setup enables thorough API testing with IP9 proxies using Insomnia's powerful features for professional development workflows.`,
      author: 'Mark Davis',
      authorRole: 'API Developer',
      publishDate: '2024-11-20',
      readTime: '12 min read',
      category: 'api-tools',
      tags: ['insomnia', 'api-testing', 'rest-client', 'testing'],
      featured: false,
      image: '/academy.png'
    }
  ]
};